/* eslint-disable react/jsx-indent-props */
import React from 'react';
import InputWithClear from '../input';
import styles from './phonebook.css';
import Icon from 'cd-ui-react/components/icon';
import PropTypes from 'prop-types';
import Tooltip from 'cd-ui-react/components/tooltip';
import ComponentsUtil, {KeyCodes} from '../../utils/ComponentsUtil';
import List from 'react-virtualized/dist/commonjs/List';
import {i18n} from '../../utils/GadgetUtils'; // utility used to get localized strings

// all the options to render the 'windowed' list using the 'react-virtualizer' library
const listOptions = {
	listHeight: 230, // Total height of the window(visible rows)
	listHeightWithError: 192, // Need to compensate the space taken up by the error/warning message
	listRowHeight: 50, // Height of each row
	listRowHeadingHeight: 80, // Height of each row which has the phonebook name as a heading
	overscanRowCount: 10, // Buffer rows to be rendered above/below the actual visible rows
	listWidth: 344, // width of the window(visible rows)
	useDynamicRowHeight: true
};

export default class PhoneBook extends React.Component {
	constructor (props) {
		super(props);
		this.numberLinkIdPrefix = 'contact-'; // HTML ID prefix for the phone number element
		this.state = {query: '',
			filteredData: this.props.phoneBookData.options,
			closeWarning: false,
			rowCount: this.props.phoneBookData.options && this.props.phoneBookData.options.length,
			currentRowScroll: 0,
			listHeight: this.props.phoneBookData.errorMessage ? listOptions.listHeightWithError : listOptions.listHeight
		};
	}

	componentDidUpdate () {
		this.phoneBookInput && ComponentsUtil.setFocusByElementID(this.phoneBookInput.props.id);
	}

	componentDidMount () {
		setTimeout(() => {
			this.phoneBookInput && ComponentsUtil.setFocusByElementID(this.phoneBookInput.props.id);
		}, 0);
	}

	rowRenderer = ({index, isScrolling, key, style}) => {
		const phoneBookOption = this.state.filteredData[index];
		return (
			<div style={style} key={index}>
				{this.constructContacts(phoneBookOption, index)}
			</div>);
	}

	getRowHeight = ({index}) => {
		return listOptions.listRowHeight;
	}

	constructContacts = (contact, index) => {
		let phoneBooks = this.state.filteredData;
		return (
			<div className={`${styles.textDefault}
			${phoneBooks.length !== index + 1 && contact.phoneBook === phoneBooks[index + 1].phoneBook
				? styles.borderBottom : ''}`} key={index} onKeyDown={this.handleKeyPress} >
				<div className="flex flex-col">
					<div className={`flex justify-content-bw ${styles.listContainer}`}>
						<Tooltip showOnlyForTruncatedText className={`flex1 ${styles.nameContainer}`} content={ComponentsUtil.encodeSpecialChars(contact.Name)}
							placement="left" chevron>
							<span className={`${styles.ellipsis} ${styles.lineHeight}`}> {contact.Name} </span>
						</Tooltip>
						<Tooltip showOnlyForTruncatedText className={`justify-content-end ${styles.numberContainer}`} content={ComponentsUtil.encodeSpecialChars(contact.DialedNumber)}
							placement="top" chevron>
								<div role="button" tabIndex="0" id={'contact-' + index}
									onClick={this.makeCall}
								data-id={index} className={`cur-pointer ${styles.textNumber} ${styles.ellipsis}`}
									aria-label={contact.Name + contact.DialedNumber}>
									{contact.DialedNumber}
							</div>
						</Tooltip>
					</div>
					<div className={styles.descriptionContainer}>
						<Tooltip showOnlyForTruncatedText content={ComponentsUtil.encodeSpecialChars(contact.Description)} className="width100" placement="left" chevron>
							<span className={`${styles.ellipsis} ${styles.subLabel}`}> {contact.Description} </span>
						</Tooltip>
					</div>
				</div>
			</div>);
	}

	onSearch = event => {
		let searchText = event.target.value;
		let searchTextArray = searchText.split(' ');
		let searchResult = this.props.phoneBookData.options.filter(contact => {
			// concatinating the columns to search flat and faster in case of an additive
			let combo = `${contact.Name} ${contact.DialedNumber} ${contact.Description || ''}`;
			// implementing the additive search where users can search for a string e.g 'phoneBookName lastName'
			for (let text of searchTextArray) {
				if (combo.toLowerCase().indexOf(text.toLowerCase()) === -1) {
					return false;
				}
			}
			return true;
		});
		this.setState({
			query: searchText,
			filteredData: searchResult,
			rowCount: searchResult.length,
			currentRowScroll: 0
		}, () => {
			this.recomputeRowHeights();
		});
	}

	recomputeRowHeights = () => {
		// recomputes the heights of the rows incase of a search
		this.contactList.recomputeRowHeights();
		this.contactList.forceUpdate();
	}

	makeCall = (e) => {
		let DialedNumber = e.target.innerText;
		this.props.phonepadActionCallback({
			number: DialedNumber
		});
	}

	handleKeyPress = event => {
		let elementID = event.target.id; // id of element with focus
		let targetIndex = parseInt(event.target.dataset.id); // row index of the element with focus
		let tabOrder = {REVERSE: -1, FORWARD: 1}; // 1 for TAB key press and -1 for TAB+SHIFT key press

		// if the TAB key is pressed while the focus is on the last row edit icon then carry on with the default tab behaviour
		if (event.keyCode === KeyCodes.KEY_TAB && targetIndex === this.state.filteredData.length - 1) {
			return;
		}

		// if SHIFT+TAB key is pressed while the focus is on the first row phone number then carry on with the default tab behaviour
		if (event.shiftKey && event.keyCode === KeyCodes.KEY_TAB &&
			elementID.indexOf(this.numberLinkIdPrefix) !== -1 && targetIndex === 0) {
			return;
		}

		// if neither of the above situation is there then prevent the default keypress (TAB, KEYUP, KEYDOWN etc) behaviour
		event.preventDefault();

		// for each key press action if the next focusable item is in a row which is not rendered yet,
		// then set the key press params so that the focus can be resumed and then trigger the rendering
		// of the next set of rows.
		if (event.keyCode === KeyCodes.KEY_DOWN) {
			this.setKeyPressParams(tabOrder.FORWARD, targetIndex, this.numberLinkIdPrefix);
			ComponentsUtil.setFocusByElementID(this.numberLinkIdPrefix + (targetIndex + 1));
		} else if (event.keyCode === KeyCodes.KEY_UP) {
			this.setKeyPressParams(tabOrder.REVERSE, targetIndex, this.numberLinkIdPrefix);
			ComponentsUtil.setFocusByElementID(this.numberLinkIdPrefix + (targetIndex - 1));
		} else if (event.keyCode === KeyCodes.KEY_ENTER || event.keyCode === KeyCodes.SPACE_BAR) {
			if (elementID.indexOf(this.numberLinkIdPrefix) !== -1) {
				this.makeCall(event);
			}
		} else if (event.keyCode === KeyCodes.KEY_TAB) {
			this.setKeyPressParams(tabOrder.FORWARD, targetIndex, this.numberLinkIdPrefix);
			this.setFocusOnTab(elementID, targetIndex, tabOrder.FORWARD);
		}
	}

	/**
	 * Sets the parameters which will be used to set the focus on the next element in case the next batch of
	 * rows gets rendered.
	 * @param tabOrder : 1 for FORWARD and -1 for REVERSE
	 * @param targetIndex : Index of the row currently focussed
	 * @param resumeKeyPressElement : Element that should have focus when the next batch of rows render
	 */
	setKeyPressParams = (tabOrder, targetIndex, resumeKeyPressElement) => {
		this.resumeKeyPress = true; // whether to resume keypress action after the next batch of rows render
		this.resumeKeyPressIndex = targetIndex + tabOrder; // Row index to focus after the next batch of rows render
		this.resumeKeyPressElement = resumeKeyPressElement; // Element of the row which needs to be focussed(phone number or the edit icon)
	}

	/**
	 * Sets the focus on the appropriate element using the Key press params while TABing
	 * @param elementID : currently focussed element
	 * @param targetIndex : Index of the row currently focussed
	 * @param tabOrder : 1 for FORWARD and -1 for REVERSE
	 */
	setFocusOnTab = (elementID, targetIndex, tabOrder) => {
		if (tabOrder === -1) {
			// If TAB is pressed when the current focus is on the phone number element, the next focusable
			// element should be the edit icon on the same row else if the current focus is on the edit icon
			// then the next focusable element should be the phone number element from the next row
			ComponentsUtil.setFocusByElementID(this.numberLinkIdPrefix + targetIndex);
		} else {
			// If TAB+SHIFT is pressed when the current focus is on the phone number element, the next focusable
			// element should be the edit icon on the row above else if the current focus is on the edit icon
			// then the next focusable element should be the phone number element from the same row
				ComponentsUtil.setFocusByElementID(this.numberLinkIdPrefix + (targetIndex + tabOrder));
		}
	}

	clearSearchText = () => {
			this.setState({
				query: '',
				filteredData: this.props.phoneBookData.options,
				rowCount: this.props.phoneBookData.options.length
			}, () => {
				this.recomputeRowHeights();
			});
	}

	hasErrorOccured = () => {
		if (this.props.phoneBookData.options) {
			return false;
		}
		return true;
	}

	renderError () {
		return (
			<div id="phonebook-error" className={`flex ${styles.errorWarningContainer}`}>
				<div style={{marginRight: 7}}><Icon name="error" size="16" color="red" /></div>
				<span id="error-text" className={`${styles.errorMsg}`}>
					{i18n.getMsg('gadget.callcontrol.contactlist.error')}
				</span>
			</div>
		);
	}

	/**
	 * Callback after the next set of rows are rendered
	 */
	onRowsRendered = () => {
		if (this.resumeKeyPress) {
			ComponentsUtil.setFocusByElementID(this.resumeKeyPressElement + this.resumeKeyPressIndex);
			this.resumeKeyPress = false;
		}
	}

	render () {
		return (
			<div role="application" className="pad-top-05r">
				<div className={styles.phonebookHeading}>{i18n.getMsg('gadget.imi.operation.Transfer.header')}</div>
				{this.hasErrorOccured() ? null :
				<div className={styles.inputContainer}>
					<InputWithClear
							id="search-field"
						type="text"
							ref={input => { this.phoneBookInput = input; }}
						maxLength="124"
						className={`${styles.searchInput}`}
							onChange={this.onSearch}
						onClear={this.clearSearchText}
							value={this.state.query}
						searchable
						placeholder=""
						role="textbox"
					/>
				</div>}
				{this.hasErrorOccured() ? this.renderError()
					: 
					<List
						className={styles.list}
						ref={(list) => {
							this.contactList = list;
						}}
						height={this.state.listHeight}
						overscanRowCount={listOptions.overscanRowCount}
						rowCount={this.state.rowCount}
						rowHeight={this.getRowHeight}
						rowRenderer={this.rowRenderer}
						onRowsRendered={this.onRowsRendered}
						scrollToIndex={this.state.currentRowScroll}
						width={listOptions.listWidth}
						tabIndex={null}
						role="list"
					/>}
					{this.props.transferError && <div className={`flex ${styles.transferdialogError}`}>
							<Icon name="error" size="12" color="red" />
							<span>{this.props.transferErrorCode === undefined ? i18n.getMsg('gadget.callcontrol.error.operation.error') 
								: i18n.getMsg('gadget.callcontrol.error.operation.error') + ' ' + this.props.transferErrorCode}</span>
						</div>}
			</div>
		);
	}
}

PhoneBook.propTypes = {
	phoneBookData: PropTypes.object,
	phonepadActionCallback: PropTypes.func
};
